import { registerBlockType } from "@wordpress/blocks";
import { useBlockProps, InspectorControls } from "@wordpress/block-editor";
import {
    PanelBody,
    RangeControl,
    ToggleControl,
    SelectControl,
    __experimentalView as View,
} from "@wordpress/components";
import ResponsiveRange from "./components/ResponsiveRange";
import CustomSwitch from "./components/CustomSwitch";
import CustomSelect from "./components/CustomSelect";
import React from "react";

// Poczekaj na zarejestrowanie oryginalnego bloku
wp.hooks.addFilter(
    "blocks.registerBlockType",
    "danone/video-attributes",
    function (settings, name) {
        if (name !== "core/video") {
            return settings;
        }

        return {
            ...settings,
            attributes: {
                ...settings.attributes,
                controls: {
                    type: "boolean",
                    default: true,
                },
                showPlayButton: {
                    type: "boolean",
                    default: true,
                },
                borderRadius: {
                    type: "number",
                    default: 0,
                },
                videoHeight: {
                    type: "number",
                    default: 5,
                },
                videoHeightUnit: {
                    type: "string",
                    default: "rem",
                },
                positionX: {
                    type: "number",
                    default: 50,
                },
                positionY: {
                    type: "number",
                    default: 50,
                },
                videoWidth: {
                    type: "number",
                    default: 100,
                },
                videoWidthUnit: {
                    type: "string",
                    default: "%",
                },
                positionTop: {
                    type: "number",
                    default: 0,
                },
                positionBottom: {
                    type: "number",
                    default: 0,
                },
                positionLeft: {
                    type: "number",
                    default: 0,
                },
                positionRight: {
                    type: "number",
                    default: 0,
                },
                positionUnit: {
                    type: "string",
                    default: "px",
                },
                usePosition: {
                    type: "boolean",
                    default: false,
                },
                playOnHover: {
                    type: "boolean",
                    default: false,
                },
                playOnClick: {
                    type: "boolean",
                    default: false,
                },
                openInModal: {
                    type: "boolean",
                    default: false,
                },
                animateOnScroll: {
                    type: "boolean",
                    default: false,
                },
                widthDesktop: {
                    type: "object",
                    default: { value: 100, valueUnit: "%" },
                },
                widthTablet: {
                    type: "object",
                    default: {},
                },
                widthMobile: {
                    type: "object",
                    default: {},
                },
                heightDesktop: {
                    type: "object",
                    default: { value: 5, valueUnit: "rem" },
                },
                heightTablet: {
                    type: "object",
                    default: {},
                },
                heightMobile: {
                    type: "object",
                    default: {},
                },
                positionDesktop: {
                    type: "object",
                    default: {},
                },
                positionTablet: {
                    type: "object",
                    default: {},
                },
                positionMobile: {
                    type: "object",
                    default: {},
                },
                position: {
                    type: "string",
                    default: "relative",
                },
                playButtonSizeDesktop: {
                    type: "object",
                    default: { value: 100, valueUnit: "%" },
                },
                playButtonSizeTablet: {
                    type: "object",
                    default: {},
                },
                playButtonSizeMobile: {
                    type: "object",
                    default: {},
                },
                positionXDesktop: {
                    type: "number",
                    default: 50,
                },
                positionXDesktopUnit: {
                    type: "string",
                    default: "%",
                },
                positionXTablet: {
                    type: "number",
                    default: null,
                },
                positionXTabletUnit: {
                    type: "string",
                    default: "%",
                },
                positionXMobile: {
                    type: "number",
                    default: null,
                },
                positionXMobileUnit: {
                    type: "string",
                    default: "%",
                },
                positionYDesktop: {
                    type: "number",
                    default: 50,
                },
                positionYDesktopUnit: {
                    type: "string",
                    default: "%",
                },
                positionYTablet: {
                    type: "number",
                    default: null,
                },
                positionYTabletUnit: {
                    type: "string",
                    default: "%",
                },
                positionYMobile: {
                    type: "number",
                    default: null,
                },
                positionYMobileUnit: {
                    type: "string",
                    default: "%",
                },
            },
            save: function (props) {
                const { attributes } = props;
                const blockProps = wp.blockEditor.useBlockProps.save({
                    className: `custom-video-player position-${
                        attributes.position
                    }${attributes.showPlayButton ? " show-play-button" : ""}${
                        attributes.animateOnScroll ? " animate-on-scroll" : ""
                    }`,
                });

                if (attributes.playOnHover) {
                    blockProps["data-play-on-hover"] = "true";
                }
                if (attributes.playOnClick) {
                    blockProps["data-play-on-click"] = "true";
                }
                if (attributes.openInModal) {
                    blockProps["data-open-modal"] = "true";
                }

                blockProps.style = {
                    "--video-border-radius": attributes.borderRadius
                        ? `${attributes.borderRadius}px`
                        : undefined,
                    "--video-height-desktop": attributes.heightDesktop?.value
                        ? `${attributes.heightDesktop.value}${attributes.heightDesktop.valueUnit}`
                        : undefined,
                    "--video-height-tablet": attributes.heightTablet?.value
                        ? `${attributes.heightTablet.value}${attributes.heightTablet.valueUnit}`
                        : undefined,
                    "--video-height-mobile": attributes.heightMobile?.value
                        ? `${attributes.heightMobile.value}${attributes.heightMobile.valueUnit}`
                        : undefined,
                    "--video-width-desktop": attributes.widthDesktop?.value
                        ? `${attributes.widthDesktop.value}${attributes.widthDesktop.valueUnit}`
                        : undefined,
                    "--video-width-tablet": attributes.widthTablet?.value
                        ? `${attributes.widthTablet.value}${attributes.widthTablet.valueUnit}`
                        : undefined,
                    "--video-width-mobile": attributes.widthMobile?.value
                        ? `${attributes.widthMobile.value}${attributes.widthMobile.valueUnit}`
                        : undefined,
                    ...(attributes.showPlayButton
                        ? {
                              "--button-x-desktop": `${
                                  attributes.positionXDesktop ||
                                  attributes.positionX
                              }${attributes.positionXDesktopUnit || "%"}`,
                              "--button-x-tablet": `${
                                  attributes.positionXTablet ||
                                  attributes.positionXDesktop ||
                                  attributes.positionX
                              }${
                                  attributes.positionXTabletUnit ||
                                  attributes.positionXDesktopUnit ||
                                  "%"
                              }`,
                              "--button-x-mobile": `${
                                  attributes.positionXMobile ||
                                  attributes.positionXTablet ||
                                  attributes.positionXDesktop ||
                                  attributes.positionX
                              }${
                                  attributes.positionXMobileUnit ||
                                  attributes.positionXTabletUnit ||
                                  attributes.positionXDesktopUnit ||
                                  "%"
                              }`,
                              "--button-y-desktop": `${
                                  attributes.positionYDesktop ||
                                  attributes.positionY
                              }${attributes.positionYDesktopUnit || "%"}`,
                              "--button-y-tablet": `${
                                  attributes.positionYTablet ||
                                  attributes.positionYDesktop ||
                                  attributes.positionY
                              }${
                                  attributes.positionYTabletUnit ||
                                  attributes.positionYDesktopUnit ||
                                  "%"
                              }`,
                              "--button-y-mobile": `${
                                  attributes.positionYMobile ||
                                  attributes.positionYTablet ||
                                  attributes.positionYDesktop ||
                                  attributes.positionY
                              }${
                                  attributes.positionYMobileUnit ||
                                  attributes.positionYTabletUnit ||
                                  attributes.positionYDesktopUnit ||
                                  "%"
                              }`,
                              "--button-scale-desktop": attributes
                                  .playButtonSizeDesktop?.value
                                  ? `${
                                        attributes.playButtonSizeDesktop.value /
                                        100
                                    }`
                                  : "1",
                              "--button-scale-tablet": attributes
                                  .playButtonSizeTablet?.value
                                  ? `${
                                        attributes.playButtonSizeTablet.value /
                                        100
                                    }`
                                  : undefined,
                              "--button-scale-mobile": attributes
                                  .playButtonSizeMobile?.value
                                  ? `${
                                        attributes.playButtonSizeMobile.value /
                                        100
                                    }`
                                  : undefined,
                          }
                        : {}),
                    ...(attributes.usePosition && {
                        "--position": "relative",
                        "--position-top-desktop": attributes.positionDesktop
                            ?.top
                            ? `${attributes.positionDesktop.top}${
                                  attributes.positionDesktop.topUnit || "px"
                              }`
                            : "auto",
                        "--position-bottom-desktop": attributes.positionDesktop
                            ?.bottom
                            ? `${attributes.positionDesktop.bottom}${
                                  attributes.positionDesktop.bottomUnit || "px"
                              }`
                            : "auto",
                        "--position-left-desktop": attributes.positionDesktop
                            ?.left
                            ? `${attributes.positionDesktop.left}${
                                  attributes.positionDesktop.leftUnit || "px"
                              }`
                            : "auto",
                        "--position-right-desktop": attributes.positionDesktop
                            ?.right
                            ? `${attributes.positionDesktop.right}${
                                  attributes.positionDesktop.rightUnit || "px"
                              }`
                            : "auto",
                        "--position-top-tablet": attributes.positionTablet?.top
                            ? `${attributes.positionTablet.top}${
                                  attributes.positionTablet.topUnit || "px"
                              }`
                            : "auto",
                        "--position-bottom-tablet": attributes.positionTablet
                            ?.bottom
                            ? `${attributes.positionTablet.bottom}${
                                  attributes.positionTablet.bottomUnit || "px"
                              }`
                            : "auto",
                        "--position-left-tablet": attributes.positionTablet
                            ?.left
                            ? `${attributes.positionTablet.left}${
                                  attributes.positionTablet.leftUnit || "px"
                              }`
                            : "auto",
                        "--position-right-tablet": attributes.positionTablet
                            ?.right
                            ? `${attributes.positionTablet.right}${
                                  attributes.positionTablet.rightUnit || "px"
                              }`
                            : "auto",
                        "--position-top-mobile": attributes.positionMobile?.top
                            ? `${attributes.positionMobile.top}${
                                  attributes.positionMobile.topUnit || "px"
                              }`
                            : "auto",
                        "--position-bottom-mobile": attributes.positionMobile
                            ?.bottom
                            ? `${attributes.positionMobile.bottom}${
                                  attributes.positionMobile.bottomUnit || "px"
                              }`
                            : "auto",
                        "--position-left-mobile": attributes.positionMobile
                            ?.left
                            ? `${attributes.positionMobile.left}${
                                  attributes.positionMobile.leftUnit || "px"
                              }`
                            : "auto",
                        "--position-right-mobile": attributes.positionMobile
                            ?.right
                            ? `${attributes.positionMobile.right}${
                                  attributes.positionMobile.rightUnit || "px"
                              }`
                            : "auto",
                    }),
                };

                if (attributes.showPlayButton) {
                    blockProps.className += " show-play-button";
                    blockProps.style = {
                        ...blockProps.style,
                        "--button-x-desktop": `${
                            attributes.positionXDesktop || attributes.positionX
                        }${attributes.positionXDesktopUnit || "%"}`,
                        "--button-x-tablet": `${
                            attributes.positionXTablet ||
                            attributes.positionXDesktop ||
                            attributes.positionX
                        }${
                            attributes.positionXTabletUnit ||
                            attributes.positionXDesktopUnit ||
                            "%"
                        }`,
                        "--button-x-mobile": `${
                            attributes.positionXMobile ||
                            attributes.positionXTablet ||
                            attributes.positionXDesktop ||
                            attributes.positionX
                        }${
                            attributes.positionXMobileUnit ||
                            attributes.positionXTabletUnit ||
                            attributes.positionXDesktopUnit ||
                            "%"
                        }`,
                        "--button-y-desktop": `${
                            attributes.positionYDesktop || attributes.positionY
                        }${attributes.positionYDesktopUnit || "%"}`,
                        "--button-y-tablet": `${
                            attributes.positionYTablet ||
                            attributes.positionYDesktop ||
                            attributes.positionY
                        }${
                            attributes.positionYTabletUnit ||
                            attributes.positionYDesktopUnit ||
                            "%"
                        }`,
                        "--button-y-mobile": `${
                            attributes.positionYMobile ||
                            attributes.positionYTablet ||
                            attributes.positionYDesktop ||
                            attributes.positionY
                        }${
                            attributes.positionYMobileUnit ||
                            attributes.positionYTabletUnit ||
                            attributes.positionYDesktopUnit ||
                            "%"
                        }`,
                    };
                }

                if (attributes.animateOnScroll) {
                    blockProps.className += " animate-on-scroll";
                }

                return wp.element.createElement(
                    "figure",
                    blockProps,
                    wp.element.createElement("video", {
                        src: attributes.src,
                        poster: attributes.poster,
                        muted: attributes.muted,
                        preload: attributes.preload,
                        loop: attributes.loop,
                        playsInline: attributes.playsInline,
                        controls:
                            !attributes.playOnHover &&
                            !attributes.playOnClick &&
                            attributes.controls,
                    })
                );
            },
        };
    }
);

const VideoControls = ({ attributes, setAttributes }) => {
    const handleUpdate = (name, device, direction, value, unit, shouldSave) => {
        if (name === "borderRadius") {
            setAttributes({ borderRadius: value });
        } else if (name === "positionX" || name === "positionY") {
            const attributeName = `${name}${
                device.charAt(0).toUpperCase() + device.slice(1)
            }`;
            if (device === "desktop") {
                setAttributes({
                    [name]: value, // Aktualizuje podstawową wartość
                    [attributeName]: value, // Aktualizuje wartość dla konkretnego urządzenia
                });
            } else {
                setAttributes({ [attributeName]: value });
            }
        }
    };

    const handleReset = (name, device) => {
        if (name === "borderRadius") {
            setAttributes({ borderRadius: 0 });
        } else if (name === "positionX") {
            const attributeName = `positionX${
                device.charAt(0).toUpperCase() + device.slice(1)
            }`;
            if (device === "desktop") {
                setAttributes({
                    positionX: 50,
                    [attributeName]: 50,
                });
            } else {
                setAttributes({ [attributeName]: null });
            }
        } else if (name === "positionY") {
            const attributeName = `positionY${
                device.charAt(0).toUpperCase() + device.slice(1)
            }`;
            if (device === "desktop") {
                setAttributes({
                    positionY: 50,
                    [attributeName]: 50,
                });
            } else {
                setAttributes({ [attributeName]: null });
            }
        }
    };

    return (
        <PanelBody title="Ustawienia odtwarzacza" initialOpen={true}>
            <View style={{ padding: "0px" }}>
                <CustomSelect
                    label="Pozycja bloku"
                    value={attributes.position}
                    options={[
                        { label: "Relative", value: "relative" },
                        { label: "Absolute", value: "absolute" },
                        { label: "Fixed", value: "fixed" },
                        { label: "Static", value: "static" },
                        { label: "Sticky", value: "sticky" },
                    ]}
                    onChange={(event) =>
                        setAttributes({ position: event.target.value })
                    }
                />

                <ResponsiveRange
                    type="single"
                    name="borderRadius"
                    tabLabel="Border Radius"
                    attributes={{
                        borderRadiusDesktop: { value: attributes.borderRadius },
                        borderRadiusTablet: { value: attributes.borderRadius },
                        borderRadiusMobile: { value: attributes.borderRadius },
                    }}
                    updateValue={handleUpdate}
                    onReset={handleReset}
                    min={0}
                    max={50}
                    step={1}
                />

                <CustomSwitch
                    label="Pokaż przycisk play"
                    checked={attributes.showPlayButton}
                    onChange={({ target: { checked } }) => {
                        setAttributes({ showPlayButton: checked });
                    }}
                />

                {attributes.showPlayButton && (
                    <>
                        <View style={{ padding: "0px" }}>
                            <ResponsiveRange
                                type="single"
                                name="positionX"
                                tabLabel="Pozycja (X)"
                                attributes={{
                                    positionXDesktop: {
                                        value:
                                            attributes.positionXDesktop ||
                                            attributes.positionX,
                                        valueUnit:
                                            attributes.positionXDesktopUnit ||
                                            "%",
                                    },
                                    positionXTablet: {
                                        value: attributes.positionXTablet,
                                        valueUnit:
                                            attributes.positionXTabletUnit,
                                    },
                                    positionXMobile: {
                                        value: attributes.positionXMobile,
                                        valueUnit:
                                            attributes.positionXMobileUnit,
                                    },
                                }}
                                updateValue={(
                                    name,
                                    device,
                                    direction,
                                    value,
                                    unit
                                ) => {
                                    const attributeName = `${name}${
                                        device.charAt(0).toUpperCase() +
                                        device.slice(1)
                                    }`;
                                    const unitName = `${attributeName}Unit`;

                                    if (device === "desktop") {
                                        setAttributes({
                                            [name]: value,
                                            [attributeName]: value,
                                            [unitName]: unit,
                                        });
                                    } else {
                                        setAttributes({
                                            [attributeName]: value,
                                            [unitName]: unit,
                                        });
                                    }
                                }}
                                onReset={handleReset}
                                min={0}
                                max={100}
                                step={1}
                                marks={[
                                    { value: 0, label: "Lewo" },
                                    { value: 50, label: "Środek" },
                                    { value: 100, label: "Prawo" },
                                ]}
                            />
                        </View>
                        <View style={{ padding: "0px" }}>
                            <ResponsiveRange
                                type="single"
                                name="positionY"
                                tabLabel="Pozycja (Y)"
                                attributes={{
                                    positionYDesktop: {
                                        value:
                                            attributes.positionYDesktop ||
                                            attributes.positionY,
                                        valueUnit:
                                            attributes.positionYDesktopUnit ||
                                            "%",
                                    },
                                    positionYTablet: {
                                        value: attributes.positionYTablet,
                                        valueUnit:
                                            attributes.positionYTabletUnit,
                                    },
                                    positionYMobile: {
                                        value: attributes.positionYMobile,
                                        valueUnit:
                                            attributes.positionYMobileUnit,
                                    },
                                }}
                                updateValue={(
                                    name,
                                    device,
                                    direction,
                                    value,
                                    unit
                                ) => {
                                    const attributeName = `${name}${
                                        device.charAt(0).toUpperCase() +
                                        device.slice(1)
                                    }`;
                                    const unitName = `${attributeName}Unit`;

                                    if (device === "desktop") {
                                        setAttributes({
                                            [name]: value,
                                            [attributeName]: value,
                                            [unitName]: unit,
                                        });
                                    } else {
                                        setAttributes({
                                            [attributeName]: value,
                                            [unitName]: unit,
                                        });
                                    }
                                }}
                                onReset={handleReset}
                                min={0}
                                max={100}
                                step={1}
                                marks={[
                                    { value: 0, label: "Góra" },
                                    { value: 50, label: "Środek" },
                                    { value: 100, label: "Dół" },
                                ]}
                            />
                        </View>
                        <View style={{ padding: "0px" }}>
                            <ResponsiveRange
                                type="single"
                                name="playButtonSize"
                                tabLabel="Wielkość przycisku"
                                attributes={attributes}
                                updateValue={(
                                    name,
                                    device,
                                    direction,
                                    value,
                                    unit
                                ) => {
                                    const attributeName = `playButtonSize${
                                        device.charAt(0).toUpperCase() +
                                        device.slice(1)
                                    }`;
                                    setAttributes({
                                        [attributeName]: {
                                            value,
                                            valueUnit: unit || "%",
                                        },
                                    });
                                }}
                                onReset={(name, device) => {
                                    const attributeName = `playButtonSize${
                                        device.charAt(0).toUpperCase() +
                                        device.slice(1)
                                    }`;
                                    setAttributes({
                                        [attributeName]:
                                            device === "desktop"
                                                ? { value: 100, valueUnit: "%" }
                                                : {},
                                    });
                                }}
                                min={50}
                                max={200}
                                step={1}
                            />
                        </View>
                    </>
                )}

                <ToggleControl
                    label="Odtwarzaj po najechaniu"
                    checked={attributes.playOnHover}
                    onChange={(value) => {
                        setAttributes({
                            playOnHover: value,
                            playOnClick: false,
                        });
                    }}
                />

                <ToggleControl
                    label="Odtwarzaj po kliknięciu"
                    checked={attributes.playOnClick}
                    onChange={(value) => {
                        setAttributes({
                            playOnClick: value,
                            playOnHover: false,
                            openInModal: false,
                        });
                    }}
                    disabled={attributes.openInModal}
                />

                <ToggleControl
                    label="Otwórz w modalu po kliknięciu"
                    checked={attributes.openInModal}
                    onChange={(value) =>
                        setAttributes({
                            openInModal: value,
                            playOnClick: false,
                        })
                    }
                    disabled={attributes.playOnClick}
                />

                <ToggleControl
                    label="Animacja przy scrollowaniu"
                    checked={attributes.animateOnScroll}
                    onChange={(value) =>
                        setAttributes({
                            animateOnScroll: value,
                        })
                    }
                />
            </View>
        </PanelBody>
    );
};

const DimensionsControls = ({ attributes, setAttributes }) => {
    const handleUpdate = (name, device, direction, value, unit, shouldSave) => {
        const attributeName = `${name}${
            device.charAt(0).toUpperCase() + device.slice(1)
        }`;
        setAttributes({ [attributeName]: { value, valueUnit: unit } });
    };

    const handleReset = (name, device) => {
        const attributeName = `${name}${
            device.charAt(0).toUpperCase() + device.slice(1)
        }`;
        setAttributes({ [attributeName]: {} });
    };

    return (
        <PanelBody title="Wymiary video" initialOpen={false}>
            <View style={{ padding: "15px" }}>
                <ResponsiveRange
                    type="single"
                    name="width"
                    tabLabel="Szerokość"
                    attributes={attributes}
                    updateValue={handleUpdate}
                    onReset={handleReset}
                    max={1000}
                />
                <ResponsiveRange
                    type="single"
                    name="height"
                    tabLabel="Wysokość"
                    attributes={attributes}
                    updateValue={handleUpdate}
                    onReset={handleReset}
                    max={1000}
                />
            </View>
        </PanelBody>
    );
};

const PositionControls = ({ attributes, setAttributes }) => {
    const handleUpdate = (name, device, direction, value, unit, shouldSave) => {
        const attributeName = `position${
            device.charAt(0).toUpperCase() + device.slice(1)
        }`;
        const newValue = {
            ...attributes[attributeName],
            [direction]: value,
            [`${direction}Unit`]: unit,
        };
        setAttributes({ [attributeName]: newValue });
    };

    const handleReset = (name, device) => {
        const attributeName = `position${
            device.charAt(0).toUpperCase() + device.slice(1)
        }`;
        setAttributes({ [attributeName]: {} });
    };

    return (
        <PanelBody title="Pozycjonowanie" initialOpen={false}>
            <View style={{ padding: "15px" }}>
                <CustomSwitch
                    label="Włącz / Wyłącz"
                    checked={attributes.usePosition}
                    onChange={({ target: { checked } }) =>
                        setAttributes({ usePosition: checked })
                    }
                />

                {attributes.usePosition && (
                    <ResponsiveRange
                        type="multiple"
                        name="position"
                        attributes={attributes}
                        updateValue={handleUpdate}
                        onReset={handleReset}
                        max={100}
                        min={-100}
                    />
                )}
            </View>
        </PanelBody>
    );
};

// Główny komponent edycji
const VideoEdit = ({ BlockEdit, ...props }) => {
    const { attributes, setAttributes, isSelected } = props;
    const blockProps = wp.blockEditor.useBlockProps({
        className: `custom-video-player position-${attributes.position}${
            attributes.showPlayButton ? " show-play-button" : ""
        }${attributes.animateOnScroll ? " animate-on-scroll" : ""}${
            isSelected ? " is-selected" : ""
        }`,
    });

    return (
        <>
            <InspectorControls>
                <VideoControls
                    attributes={attributes}
                    setAttributes={setAttributes}
                />
                <DimensionsControls
                    attributes={attributes}
                    setAttributes={setAttributes}
                />
                <PositionControls
                    attributes={attributes}
                    setAttributes={setAttributes}
                />
            </InspectorControls>
            <figure
                {...blockProps}
                data-play-on-hover={attributes.playOnHover ? "true" : undefined}
                data-play-on-click={attributes.playOnClick ? "true" : undefined}
                data-open-modal={attributes.openInModal ? "true" : undefined}
                style={{
                    ...blockProps.style,
                    "--video-border-radius": attributes.borderRadius
                        ? `${attributes.borderRadius}px`
                        : undefined,
                    "--video-height-desktop": attributes.heightDesktop?.value
                        ? `${attributes.heightDesktop.value}${attributes.heightDesktop.valueUnit}`
                        : undefined,
                    "--video-height-tablet": attributes.heightTablet?.value
                        ? `${attributes.heightTablet.value}${attributes.heightTablet.valueUnit}`
                        : undefined,
                    "--video-height-mobile": attributes.heightMobile?.value
                        ? `${attributes.heightMobile.value}${attributes.heightMobile.valueUnit}`
                        : undefined,
                    "--video-width-desktop": attributes.widthDesktop?.value
                        ? `${attributes.widthDesktop.value}${attributes.widthDesktop.valueUnit}`
                        : undefined,
                    "--video-width-tablet": attributes.widthTablet?.value
                        ? `${attributes.widthTablet.value}${attributes.widthTablet.valueUnit}`
                        : undefined,
                    "--video-width-mobile": attributes.widthMobile?.value
                        ? `${attributes.widthMobile.value}${attributes.widthMobile.valueUnit}`
                        : undefined,
                    ...(attributes.showPlayButton
                        ? {
                              "--button-x-desktop": `${
                                  attributes.positionXDesktop ||
                                  attributes.positionX
                              }${attributes.positionXDesktopUnit || "%"}`,
                              "--button-x-tablet": `${
                                  attributes.positionXTablet ||
                                  attributes.positionXDesktop ||
                                  attributes.positionX
                              }${
                                  attributes.positionXTabletUnit ||
                                  attributes.positionXDesktopUnit ||
                                  "%"
                              }`,
                              "--button-x-mobile": `${
                                  attributes.positionXMobile ||
                                  attributes.positionXTablet ||
                                  attributes.positionXDesktop ||
                                  attributes.positionX
                              }${
                                  attributes.positionXMobileUnit ||
                                  attributes.positionXTabletUnit ||
                                  attributes.positionXDesktopUnit ||
                                  "%"
                              }`,
                              "--button-y-desktop": `${
                                  attributes.positionYDesktop ||
                                  attributes.positionY
                              }${attributes.positionYDesktopUnit || "%"}`,
                              "--button-y-tablet": `${
                                  attributes.positionYTablet ||
                                  attributes.positionYDesktop ||
                                  attributes.positionY
                              }${
                                  attributes.positionYTabletUnit ||
                                  attributes.positionYDesktopUnit ||
                                  "%"
                              }`,
                              "--button-y-mobile": `${
                                  attributes.positionYMobile ||
                                  attributes.positionYTablet ||
                                  attributes.positionYDesktop ||
                                  attributes.positionY
                              }${
                                  attributes.positionYMobileUnit ||
                                  attributes.positionYTabletUnit ||
                                  attributes.positionYDesktopUnit ||
                                  "%"
                              }`,
                              "--button-scale-desktop": attributes
                                  .playButtonSizeDesktop?.value
                                  ? `${
                                        attributes.playButtonSizeDesktop.value /
                                        100
                                    }`
                                  : "1",
                              "--button-scale-tablet": attributes
                                  .playButtonSizeTablet?.value
                                  ? `${
                                        attributes.playButtonSizeTablet.value /
                                        100
                                    }`
                                  : undefined,
                              "--button-scale-mobile": attributes
                                  .playButtonSizeMobile?.value
                                  ? `${
                                        attributes.playButtonSizeMobile.value /
                                        100
                                    }`
                                  : undefined,
                          }
                        : {}),
                    ...(attributes.usePosition && {
                        "--position-top-desktop": attributes.positionDesktop
                            ?.top
                            ? `${attributes.positionDesktop.top}${
                                  attributes.positionDesktop.topUnit || "px"
                              }`
                            : "auto",
                        "--position-bottom-desktop": attributes.positionDesktop
                            ?.bottom
                            ? `${attributes.positionDesktop.bottom}${
                                  attributes.positionDesktop.bottomUnit || "px"
                              }`
                            : "auto",
                        "--position-left-desktop": attributes.positionDesktop
                            ?.left
                            ? `${attributes.positionDesktop.left}${
                                  attributes.positionDesktop.leftUnit || "px"
                              }`
                            : "auto",
                        "--position-right-desktop": attributes.positionDesktop
                            ?.right
                            ? `${attributes.positionDesktop.right}${
                                  attributes.positionDesktop.rightUnit || "px"
                              }`
                            : "auto",
                        "--position-top-tablet": attributes.positionTablet?.top
                            ? `${attributes.positionTablet.top}${
                                  attributes.positionTablet.topUnit || "px"
                              }`
                            : "auto",
                        "--position-bottom-tablet": attributes.positionTablet
                            ?.bottom
                            ? `${attributes.positionTablet.bottom}${
                                  attributes.positionTablet.bottomUnit || "px"
                              }`
                            : "auto",
                        "--position-left-tablet": attributes.positionTablet
                            ?.left
                            ? `${attributes.positionTablet.left}${
                                  attributes.positionTablet.leftUnit || "px"
                              }`
                            : "auto",
                        "--position-right-tablet": attributes.positionTablet
                            ?.right
                            ? `${attributes.positionTablet.right}${
                                  attributes.positionTablet.rightUnit || "px"
                              }`
                            : "auto",
                        "--position-top-mobile": attributes.positionMobile?.top
                            ? `${attributes.positionMobile.top}${
                                  attributes.positionMobile.topUnit || "px"
                              }`
                            : "auto",
                        "--position-bottom-mobile": attributes.positionMobile
                            ?.bottom
                            ? `${attributes.positionMobile.bottom}${
                                  attributes.positionMobile.bottomUnit || "px"
                              }`
                            : "auto",
                        "--position-left-mobile": attributes.positionMobile
                            ?.left
                            ? `${attributes.positionMobile.left}${
                                  attributes.positionMobile.leftUnit || "px"
                              }`
                            : "auto",
                        "--position-right-mobile": attributes.positionMobile
                            ?.right
                            ? `${attributes.positionMobile.right}${
                                  attributes.positionMobile.rightUnit || "px"
                              }`
                            : "auto",
                    }),
                }}
            >
                <video
                    src={attributes.src}
                    poster={attributes.poster}
                    controls={
                        !attributes.playOnHover &&
                        !attributes.playOnClick &&
                        attributes.controls
                    }
                />
            </figure>
        </>
    );
};

// Rejestracja bloku
const withVideoControls = wp.compose.createHigherOrderComponent((BlockEdit) => {
    return (props) => {
        if (props.name !== "core/video") {
            return <BlockEdit {...props} />;
        }
        return <VideoEdit BlockEdit={BlockEdit} {...props} />;
    };
}, "withVideoControls");

wp.hooks.addFilter(
    "editor.BlockEdit",
    "danone/with-video-controls",
    withVideoControls
);
